import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

import type { Option } from "../../constants";
import type { RuleVariable, SimpleVariableInput } from "../../hooks/useRuleVariables";
import type { ScreenLocationOption } from "../../modals/CreateRuleModal";
import type { OperatorOption } from "../../types/configs";
import type { LoopStackEntry } from "./loopTypes";

// ─── Form config passed from RuleBuilder ─────────────────────────────────────

export type LoopFormConfig = {
  platformId?: string | number;
  /**
   * Trimmed names of every loop node in the entire rule graph (all canvas levels).
   * Passed to LoopForm so it can enforce globally-unique loop names.
   */
  allLoopNames: Set<string>;
  /**
   * Lowercased names of every reaction, assignment, and decision node in the entire rule graph.
   * Used for globally-unique default names and validation (same idea as `allLoopNames`).
   */
  allReactionAssignmentDecisionNames: Set<string>;
  /** Raw operator options for forms that need the OperatorOption shape (id + operator). */
  comparativeOperators: OperatorOption[];
  comparativeOperatorOptions: { label: string; value: string }[];
  assignmentOperatorOptions: { label: string; value: string }[];
  globalConstantOptions: { label: string; value: string }[];
  formulaOperatorOptions: unknown[];
  expressionFunctionOptions: unknown[];
  reactionTypeOptions: Option[];
  ruleFieldOptions: { label: string; value: string; [key: string]: unknown }[];
  advancedTriggerOptions: { label: string; value: string; [key: string]: unknown }[];
  collectionOptions: { label: string; value: string; [key: string]: unknown }[];
  screenLocationOptions: ScreenLocationOption[];
  ruleScreenLocationId?: string | number;
  resourceVariableGroups: { label: string; options: { label: string; value: string; dataType?: string }[] }[];
  formulaVariableOptions: { label: string; value: string; dataType?: string }[];
  singleRowDatabaseVariableOptions: { label: string; value: string; dataType?: string }[];
  databaseMultiRow: RuleVariable[];
  databaseSingleRow: RuleVariable[];
  simple: RuleVariable[];
  formula: RuleVariable[];
  reaction: RuleVariable[];
  allVariables: RuleVariable[];
  reactionVariableOptions: { label: string; value: string }[];
  addSimpleVariable?: (input: SimpleVariableInput) => RuleVariable | null;
};

// ─── Navigation context ───────────────────────────────────────────────────────

type LoopNavigationContextValue = {
  loopStack: LoopStackEntry[];
  enterLoop: (loopNodeId: string, loopName: string, loopType?: "variable" | "table") => void;
  /** Navigate to a specific breadcrumb index. Pass -1 to return to the main canvas. */
  exitTo: (index: number) => void;
};

const LoopNavigationContext = createContext<LoopNavigationContextValue | undefined>(undefined);

// ─── Form config context ──────────────────────────────────────────────────────

const LoopFormConfigContext = createContext<LoopFormConfig | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

interface LoopNavigationProviderProps {
  formConfig: LoopFormConfig;
}

export const LoopNavigationProvider = ({
	children,
	formConfig,
}: PropsWithChildren<LoopNavigationProviderProps>) => {
	const [loopStack, setLoopStack] = useState<LoopStackEntry[]>([]);

	const enterLoop = useCallback((loopNodeId: string, loopName: string, loopType?: "variable" | "table") => {
		setLoopStack((prev) => [...prev, { loopNodeId, loopName, loopType: loopType ?? "variable" }]);
	}, []);

	const exitTo = useCallback((index: number) => {
		if (index < 0) {
			setLoopStack([]);
		} else {
			setLoopStack(prev => prev.slice(0, index + 1));
		}
	}, []);

	const navValue = useMemo<LoopNavigationContextValue>(
		() => ({ loopStack, enterLoop, exitTo }),
		[loopStack, enterLoop, exitTo],
	);

	return (
		<LoopFormConfigContext.Provider value={formConfig}>
			<LoopNavigationContext.Provider value={navValue}>
				{children}
			</LoopNavigationContext.Provider>
		</LoopFormConfigContext.Provider>
	);
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const useLoopNavigation = (): LoopNavigationContextValue => {
	const ctx = useContext(LoopNavigationContext);
	if (!ctx) throw new Error("useLoopNavigation must be used within LoopNavigationProvider");
	return ctx;
};

export const useLoopFormConfig = (): LoopFormConfig => {
	const ctx = useContext(LoopFormConfigContext);
	if (!ctx) throw new Error("useLoopFormConfig must be used within LoopNavigationProvider");
	return ctx;
};
